import { useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  clientId,
  locationSupport,
  permissionState,
  pingBackend,
  startWatching,
} from "@/lib/location";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";

const SIG_MOVE_M = 400; // re-ping only when we've moved more than 400 m
const POLL_MS = 5 * 60 * 1000; // fallback safety poll while app is open

function dist(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const p1 = (a.lat * Math.PI) / 180;
  const p2 = (b.lat * Math.PI) / 180;
  const dP = ((b.lat - a.lat) * Math.PI) / 180;
  const dL = ((b.lon - a.lon) * Math.PI) / 180;
  const h = Math.sin(dP / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dL / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

async function showAndreasNotification(payload) {
  // Try SW-based notification first (works when tab is backgrounded on Android).
  if ("serviceWorker" in navigator && Notification && Notification.permission === "granted") {
    try {
      const reg = await navigator.serviceWorker.getRegistration("/service-worker.js");
      if (reg) {
        await reg.showNotification(payload.title, {
          body: payload.body,
          icon: "/icons/icon-192.png",
          badge: "/icons/badge-96.png",
          tag: payload.tag || "andreas-location",
          data: { url: "/" },
          vibrate: [80, 40, 80],
        });
        return true;
      }
    } catch { /* fall through */ }
  }
  // Fallback: in-app toast so the guest still sees Andreas.
  toast.success(`${payload.title} — ${payload.body}`);
  return false;
}

const LocationPinger = () => {
  const { lang } = useLang();
  const { isAuthed, authReady } = useAuth();
  const lastRef = useRef(null);
  const stopRef = useRef(null);

  useEffect(() => {
    if (!authReady || !isAuthed) return;
    if (!locationSupport()) return;

    let cancelled = false;
    let intervalId;

    const doPing = async (coords) => {
      if (cancelled || !coords) return;
      const now = { lat: coords.latitude, lon: coords.longitude };
      // Skip if we haven't moved meaningfully since last ping.
      if (lastRef.current && dist(lastRef.current, now) < SIG_MOVE_M) return;
      lastRef.current = now;
      try {
        const res = await pingBackend({
          lat: now.lat,
          lon: now.lon,
          accuracy: coords.accuracy,
          lang,
        });
        if (res && res.message) {
          await showAndreasNotification(res.message);
        }
      } catch (e) {
        // Silent — location pings are best-effort.
      }
    };

    const start = async () => {
      const state = await permissionState();
      if (state !== "granted") return;
      // 1. One-shot ping on app open.
      navigator.geolocation.getCurrentPosition(
        (pos) => doPing(pos.coords),
        () => {},
        { enableHighAccuracy: false, maximumAge: 30_000, timeout: 15_000 }
      );
      // 2. Continuous watch while app is open.
      stopRef.current = startWatching((pos) => doPing(pos.coords));
      // 3. Safety fallback poll (some browsers throttle watchPosition).
      intervalId = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
          (pos) => doPing(pos.coords),
          () => {},
          { enableHighAccuracy: false, maximumAge: 60_000, timeout: 15_000 }
        );
      }, POLL_MS);
    };

    start();
    // Ensure a stable client id exists early.
    clientId();

    return () => {
      cancelled = true;
      if (stopRef.current) stopRef.current();
      if (intervalId) clearInterval(intervalId);
    };
  }, [authReady, isAuthed, lang]);

  return null;
};

export default LocationPinger;
